import { Eye, EyeOff, Pin, PinOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { SubjectIcon } from "@/components/subject-icon";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

export type LearnerSubject = {
  id: string;
  name: string;
  icon: string | null;
  active: boolean;
  practised: boolean;
  pinned: boolean;
  hidden: boolean;
  available: boolean;
  recommended: boolean;
  lastSolvedAt: string | null;
  strands?: SubjectStrand[];
};

/** One part of a subject (Lithuanian: kalba, literatūra, skaitymas ir rašymas)
    and whether this learner's sessions draw from it. */
export type SubjectStrand = {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  skillCount: number;
};

export type PinState = "pinned" | "hidden" | null;

/**
 * The row of subjects this learner does not work in.
 *
 * All six subjects used to be laid out in full on this page — 501 skill cards,
 * of which one child had ever touched 47. Physics, chemistry and geography are
 * not wrong to exist; they are wrong to be the same size as the subject the
 * child actually studies. They fold down to this until somebody says otherwise.
 */
export function InactiveSubjectsStrip({
  subjects,
  skillCounts,
  onPin,
  disabled,
}: {
  subjects: LearnerSubject[];
  skillCounts: Record<string, number>;
  onPin: (subjectId: string, state: PinState) => void;
  disabled?: boolean;
}) {
  if (subjects.length === 0) return null;

  return (
    <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-3">
      <p className="mb-2 text-xs text-muted-foreground">
        Kiti dalykai — mokinys jų dar nesimoko. Paspausk, kad rodytų.
      </p>
      <div className="flex flex-wrap gap-2">
        {subjects.map((subject) => (
          <Button
            key={subject.id}
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => onPin(subject.id, subject.hidden ? null : "pinned")}
            className="h-8 gap-1.5 text-xs font-normal"
          >
            <SubjectIcon icon={subject.icon ?? "BookOpen"} className="h-3.5 w-3.5" />
            {subject.name}
            <span className="text-muted-foreground">{skillCounts[subject.id] ?? 0}</span>
            {subject.hidden ? (
              <Eye className="h-3 w-3 text-muted-foreground" />
            ) : (
              <Pin className="h-3 w-3 text-muted-foreground" />
            )}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * Why a subject is on screen, and how to disagree. Two different overrides:
 * pinning a subject nobody has touched, and hiding one they have finished with.
 */
export function SubjectVisibilityButton({
  subject,
  onPin,
  disabled,
}: {
  subject: LearnerSubject;
  onPin: (subjectId: string, state: PinState) => void;
  disabled?: boolean;
}) {
  // A pinned subject clears back to automatic; one that is here on its own
  // merits can only be pushed away by saying so.
  const clearing = subject.pinned;
  const Icon = clearing ? PinOff : EyeOff;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onPin(subject.id, clearing ? null : "hidden");
          }}
          className="h-7 w-7 text-muted-foreground/50 hover:text-foreground"
        >
          <Icon className="h-3.5 w-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {clearing ? "Nebelaikyti prisegto" : "Slėpti šį dalyką šiam mokiniui"}
      </TooltipContent>
    </Tooltip>
  );
}
